<template>
  <div class="flex-1 flex flex-col relative w-full">
    <!-- Barra de Progresso (Absoluta no topo) -->
    <div v-if="isSearching || (searchProgress.status && searchProgress.status !== 'idle')" class="absolute top-0 left-0 w-full z-50">
      <div class="h-[2px] w-full bg-transparent overflow-hidden">
        <div 
            :class="[
            'h-full transition-all duration-300 ease-out',
            searchProgress.status === 'error' ? 'bg-red-500' : 'bg-transparent'
          ]"
          :style="{ width: searchProgress.progress + '%' }"
        ></div>
      </div>
    </div>

    <!-- Campo de Busca: Teleport para header quando há resultados -->
    <Teleport v-if="hasEverHadSearchResults" to="#header-search-slot">
      <div class="w-full max-w-xl mx-auto">
        <div class="relative w-full">
          <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg class="h-4 w-4 text-[var(--bl-muted)]" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
            </svg>
          </div>
          <input
            v-model="searchQuery"
            @keyup.enter="performSearch"
            class="bl-search w-full border-0 pl-10 pr-11 py-2.5 text-sm"
            placeholder="Pesquisar na rede P2P…"
            :disabled="isSearching || isReconnecting"
          >
          <button
            type="button"
            @click="performSearch"
            :disabled="isSearching || isReconnecting || !searchQuery.trim()"
            :class="[
              'absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full p-1.5 transition-all duration-200',
              isSearching || isReconnecting || !searchQuery.trim()
                ? 'opacity-40 cursor-not-allowed text-[var(--bl-muted)]'
                : 'bl-btn-accent hover:opacity-95'
            ]"
            title="Buscar"
          >
            <span v-if="isSearching || isReconnecting" class="block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
            <svg v-else class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>
      </div>
    </Teleport>

    <!-- Home estilo Brave: mascote + wordmark + barra central -->
    <div 
      v-if="!hasEverHadSearchResults"
      class="fade-in flex flex-col w-full min-h-[62vh] justify-center"
    >
      <div class="container mx-auto px-4">
        <div class="max-w-2xl mx-auto">
          <div class="text-center mb-8">
            <img
              src="@/assets/img/logo.png"
              alt="BuscaLogo"
              class="w-28 h-28 md:w-36 md:h-36 mx-auto mb-5 object-contain drop-shadow-md select-none"
              draggable="false"
            >
            <h1 class="text-4xl md:text-5xl font-extrabold tracking-tight mb-2">
              Busca<span class="text-[var(--bl-amber)]">Logo</span>
            </h1>
            <p class="text-sm md:text-base text-[var(--bl-muted)]">
              Busca colaborativa P2P
            </p>
          </div>

          <div class="flex justify-center mb-5">
            <div class="relative w-full">
              <div class="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-[var(--bl-muted)]" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                </svg>
              </div>
              <input
                v-model="searchQuery"
                @keyup.enter="performSearch"
                class="bl-search w-full border-0 pl-12 pr-14 py-4 text-lg"
                placeholder="Pesquisar na rede P2P…"
                :disabled="isSearching || isReconnecting"
                autofocus
              >
              <button
                type="button"
                @click="performSearch"
                :disabled="isSearching || isReconnecting || !searchQuery.trim()"
                :class="[
                  'absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-2.5 transition-all duration-200',
                  isSearching || isReconnecting || !searchQuery.trim()
                    ? 'opacity-40 cursor-not-allowed text-[var(--bl-muted)]'
                    : 'bl-btn-accent shadow-sm hover:opacity-95'
                ]"
                title="Buscar"
              >
                <span v-if="isReconnecting || isSearching" class="block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </div>
          </div>

          <div v-if="isReconnecting" class="text-center mb-4">
            <div class="flex items-center justify-center gap-2 text-sm text-[var(--bl-amber)]">
              <span class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
              Reconectando… ({{ reconnectionAttempts }}/{{ maxReconnectionAttempts }})
            </div>
          </div>

          <DefaultSearchHint />
        </div>
      </div>
    </div>

    <div
      class="flex-1 w-full"
      :class="hasEverHadSearchResults ? 'py-2' : 'py-6'"
    >
      <div v-if="(isSearching || (searchProgress.status && searchProgress.status !== 'idle')) && !hasEverHadSearchResults" class="max-w-2xl mx-auto px-4 text-center mt-6">
        <p class="text-sm font-medium flex items-center justify-center gap-2 text-[var(--bl-muted)]">
          <span class="loading-dots text-[var(--bl-amber)]"><span></span><span></span><span></span></span>
          {{ progressText }}
          <span v-if="searchProgress.peersCount > 0" class="opacity-75 ml-2 font-normal text-xs">
            {{ searchProgress.peersResponded || 0 }}/{{ searchProgress.peersCount }} peers
            <span v-if="searchProgress.progress">({{ searchProgress.progress }}%)</span>
          </span>
        </p>
      </div>

      <div v-if="searchResults.length > 0" class="w-full">
        <div class="max-w-[42rem] mx-auto md:mx-0 md:ml-[max(1rem,calc((100%-56rem)/2))] px-4 md:pl-6 lg:pl-8">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 border-b border-[var(--bl-border)]/70 mb-1">
            <p class="text-[13px] text-[var(--bl-muted)]">
              About {{ displayedTotal.toLocaleString('pt-BR') }} results
              <span v-if="searchTime > 0 && !isSearching" class="opacity-70">({{ (searchTime / 1000).toFixed(2) }} seconds)</span>
              <span v-if="isSearching || (searchProgress.status && ['started','progress'].includes(searchProgress.status))" class="ml-2 inline-flex items-center gap-1.5 text-[var(--bl-amber)]">
                <span class="loading-dots"><span></span><span></span><span></span></span>
                <span class="text-xs">Atualizando…</span>
              </span>
            </p>
            <div class="flex flex-wrap items-center gap-2">
              <select
                v-model="filterDomain"
                @change="currentPage = 1"
                class="text-[13px] px-2.5 py-1.5 rounded-lg border border-[var(--bl-border)] bg-[var(--bl-surface)] text-[var(--bl-ink)] cursor-pointer"
              >
                <option value="">Todos os sites</option>
                <option v-for="d in uniqueDomains" :key="d.domain" :value="d.domain">
                  {{ d.domain }} ({{ d.count }})
                </option>
              </select>
              <select
                v-model="sortBy"
                @change="currentPage = 1"
                class="text-[13px] px-2.5 py-1.5 rounded-lg border border-[var(--bl-border)] bg-[var(--bl-surface)] text-[var(--bl-ink)] cursor-pointer"
              >
                <option value="relevance">Relevância</option>
                <option value="date">Mais recente</option>
                <option value="domain">Por domínio</option>
              </select>
            </div>
          </div>

          <div v-if="isSearching || (searchProgress.status && ['started','progress'].includes(searchProgress.status))" class="space-y-7 py-6">
            <div v-for="i in 5" :key="i" class="space-y-2">
              <div class="flex items-center gap-2">
                <div class="w-5 h-5 rounded-full animate-pulse bg-[var(--bl-border)]"></div>
                <div class="h-3 w-40 rounded animate-pulse bg-[var(--bl-border)]"></div>
              </div>
              <div class="h-5 w-4/5 rounded animate-pulse bg-[var(--bl-border)]"></div>
              <div class="h-3 w-full rounded animate-pulse bg-[var(--bl-border)]/60"></div>
              <div class="h-3 w-2/3 rounded animate-pulse bg-[var(--bl-border)]/60"></div>
            </div>
          </div>

          <div v-else class="py-4">
            <template v-if="sortBy === 'domain'">
              <div v-for="group in paginatedResultsByDomain" :key="group.baseDomain" class="mb-8">
                <div class="flex items-baseline gap-2 mb-4 pb-2 border-b border-[var(--bl-border)]/50">
                  <span class="text-xs font-semibold uppercase tracking-wider text-[var(--bl-amber)]">{{ group.baseDomain }}</span>
                  <span class="text-xs text-[var(--bl-muted)]">{{ group.results.length }} {{ group.results.length === 1 ? 'resultado' : 'resultados' }}</span>
                </div>
                <div class="space-y-7">
                  <ResultCard v-for="result in group.results" :key="result.id" :result="result" />
                </div>
              </div>
            </template>
            <div v-else class="space-y-7">
              <ResultCard v-for="result in paginatedResults" :key="result.id" :result="result" />
            </div>
          </div>

          <div
            v-if="calculatedTotalPages > 1 && !isSearching && !['started','progress'].includes(searchProgress.status)"
            class="mt-8 mb-10 flex justify-start"
          >
            <nav class="flex items-center gap-1.5">
              <button type="button" @click="goToPage(currentPage - 1)" :disabled="currentPage <= 1"
                :class="['px-3 py-2 rounded-lg text-sm font-medium transition-colors', currentPage <= 1 ? 'opacity-40 cursor-not-allowed text-[var(--bl-muted)]' : 'text-[var(--bl-ink)] hover:bg-[var(--bl-border)]/40']">
                ← Anterior
              </button>
              <button v-for="page in visiblePages" :key="page" type="button" @click="goToPage(page)"
                :class="['min-w-[2.25rem] px-2.5 py-2 rounded-lg text-sm font-medium transition-colors', page === currentPage ? 'bl-btn-accent text-white' : 'text-[var(--bl-ink)] hover:bg-[var(--bl-border)]/40']">
                {{ page }}
              </button>
              <button type="button" @click="goToPage(currentPage + 1)" :disabled="currentPage >= calculatedTotalPages"
                :class="['px-3 py-2 rounded-lg text-sm font-medium transition-colors', currentPage >= calculatedTotalPages ? 'opacity-40 cursor-not-allowed text-[var(--bl-muted)]' : 'text-[var(--bl-ink)] hover:bg-[var(--bl-border)]/40']">
                Próximo →
              </button>
            </nav>
          </div>
        </div>
      </div>

      <div v-else-if="!isSearching && searchResults.length === 0" class="max-w-xl mx-auto px-4 text-center mt-12">
        <p class="text-[15px] mb-8 text-[var(--bl-muted)]">
          {{ hasEverHadSearchResults ? `Nenhum resultado encontrado para "${searchQuery || searchProgress.query || ''}".` : 'Busque para explorar o conhecimento colaborativo indexado pelas extensões conectadas.' }}
        </p>
        <router-link v-if="!hasEverHadSearchResults" to="/instalar-extensao"
          class="inline-flex items-center px-6 py-2.5 rounded-full text-sm font-medium bl-btn-accent transition-all active:scale-95">
          Instalar extensão e Agent
        </router-link>
      </div>

      <div v-if="searchProgress.status === 'error'" class="max-w-2xl mx-auto px-4 text-center mt-6">
        <div class="p-5 rounded-2xl border border-red-300/50 bg-red-50 text-red-700 dark:bg-red-900/20 dark:border-red-700 dark:text-red-300">
          <h3 class="text-base font-semibold mb-1">Erro na busca</h3>
          <p class="text-sm">{{ searchProgress.error }}</p>
        </div>
      </div>

      <div v-if="searchProgress.status === 'no_peers'" class="max-w-2xl mx-auto px-4 text-center mt-6">
        <div class="p-5 rounded-2xl border border-[var(--bl-amber)]/30 bg-[var(--bl-amber-soft)] text-[var(--bl-ink)]">
          <h3 class="text-base font-semibold mb-1">Nenhuma extensão conectada</h3>
          <p class="text-sm text-[var(--bl-muted)]">Instale a extensão BuscaLogo para buscar em páginas indexadas pela rede.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, inject, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import P2PClient from '../p2p-client.js'
import ResultCard from './ResultCard.vue'
import DefaultSearchHint from './DefaultSearchHint.vue'

// Injeta do App.vue
const updateAppConnectionStatus = inject('updateAppConnectionStatus', () => {})
const isDarkMode = inject('isDarkMode', ref(false))
const resetHomeRef = inject('resetHome', ref(null))
const route = useRoute()
const router = useRouter()

const readQueryParam = () => {
  const q = route.query.q
  if (typeof q === 'string') return q.trim()
  if (Array.isArray(q) && typeof q[0] === 'string') return q[0].trim()
  return ''
}

const syncQueryToUrl = (term) => {
  const q = (term || '').trim()
  if (!q) {
    if (route.path === '/search' || route.query.q) {
      router.replace({ path: '/' })
    }
    return
  }
  if (route.path !== '/search' || route.query.q !== q) {
    router.replace({ path: '/search', query: { q } })
  }
}

const resetToInitial = () => {
  searchResults.value = []
  hasEverHadSearchResults.value = false
  searchQuery.value = ''
  searchProgress.value = {}
  currentPage.value = 1
  filterDomain.value = ''
  syncQueryToUrl('')
}

// Estado da aplicação
const searchQuery = ref('')
const isSearching = ref(false)
const searchResults = ref([])
const hasEverHadSearchResults = ref(false)
const searchProgress = ref({})
const searchTime = ref(0)
const currentPage = ref(1)
const totalResults = ref(0)
const resultsPerPage = ref(10)
const sortBy = ref('relevance')
const filterDomain = ref('')

// Estado para reconexão automática
const isReconnecting = ref(false)
const reconnectionAttempts = ref(0)
const maxReconnectionAttempts = 3

const displayedTotal = computed(() => filterDomain.value ? filteredResults.value.length : totalResults.value)

const calculatedTotalPages = computed(() => {
  return Math.ceil(displayedTotal.value / resultsPerPage.value) || 1
})

// Layout: mantém modo busca após primeira pesquisa (sem alterar ao fazer nova busca)
const hasSearchResults = computed(() => searchResults.value.length > 0)

// Cliente P2P
const p2pClient = ref(null)

// Computed properties
const currentQuery = computed(() => {
  return searchProgress.value.query || ''
})

const progressText = computed(() => {
  if (searchProgress.value.status === 'started') return 'Iniciando busca...'
  if (searchProgress.value.status === 'progress') return 'Buscando em peers...'
  if (searchProgress.value.status === 'completed') return 'Busca concluída'
  if (searchProgress.value.status === 'no_peers') return 'Nenhum peer conectado'
  if (searchProgress.value.status === 'error') return 'Erro na busca'
  return 'Preparando busca...'
})

const getBaseDomain = (hostname) => {
  if (!hostname) return ''
  const parts = hostname.split('.')
  if (parts.length <= 2) return hostname
  const multiPartTlds = ['com.br', 'co.uk', 'com.au', 'co.jp', 'net.br', 'org.br']
  if (parts.length >= 3 && multiPartTlds.some(m => hostname.endsWith('.' + m))) {
    return parts.slice(-3).join('.')
  }
  return parts.slice(-2).join('.')
}

const isBaseDomain = (hostname) => {
  const base = getBaseDomain(hostname)
  return hostname === base
}

const getResultHostnameForSort = (result) => {
  if (result.metadata?.hostname) return result.metadata.hostname
  if (result.hostname) return result.hostname
  try {
    return result.url ? new URL(result.url).hostname : ''
  } catch {
    return ''
  }
}

/** 2=raiz sem /, 1=raiz com /, 0=tem path/hash */
const getRootPriority = (url) => {
  if (!url) return 0
  try {
    const u = new URL(url)
    const path = u.pathname || ''
    const hash = (u.hash || '').trim()
    if (hash.length > 1) return 0
    if (path === '') return 2
    if (path === '/') return 1
    return 0
  } catch {
    return 0
  }
}

const uniqueDomains = computed(() => {
  const counts = new Map()
  for (const r of searchResults.value) {
    const host = getResultHostnameForSort(r)
    const base = getBaseDomain(host)
    if (base) counts.set(base, (counts.get(base) || 0) + 1)
  }
  return Array.from(counts.entries())
    .map(([domain, count]) => ({ domain, count }))
    .sort((a, b) => b.count - a.count)
})

const filteredResults = computed(() => {
  if (!filterDomain.value) return searchResults.value
  return searchResults.value.filter(r => {
    const host = getResultHostnameForSort(r)
    const base = getBaseDomain(host)
    return base === filterDomain.value
  })
})

const sortedResults = computed(() => {
  const results = [...filteredResults.value]
  const sort = sortBy.value

  if (sort === 'relevance') {
    return results.sort((a, b) => {
      const relA = a.relevance ?? a.score ?? 0
      const relB = b.relevance ?? b.score ?? 0
      if (relB !== relA) return relB - relA
      const tsA = new Date(a.metadata?.scrapedAt || a.timestamp || 0)
      const tsB = new Date(b.metadata?.scrapedAt || b.timestamp || 0)
      return tsB - tsA
    })
  }

  if (sort === 'date') {
    return results.sort((a, b) => {
      const tsA = new Date(a.metadata?.scrapedAt || a.created_at || a.timestamp || 0)
      const tsB = new Date(b.metadata?.scrapedAt || b.created_at || b.timestamp || 0)
      return tsB - tsA
    })
  }

  if (sort === 'domain') {
    return results.sort((a, b) => {
      const hostA = getResultHostnameForSort(a)
      const hostB = getResultHostnameForSort(b)
      const baseA = getBaseDomain(hostA)
      const baseB = getBaseDomain(hostB)
      if (baseA !== baseB) return baseA.localeCompare(baseB)
      const isBaseA = isBaseDomain(hostA)
      const isBaseB = isBaseDomain(hostB)
      if (isBaseA && !isBaseB) return -1
      if (!isBaseA && isBaseB) return 1
      const rootA = getRootPriority(a.url || a.link || '')
      const rootB = getRootPriority(b.url || b.link || '')
      if (rootA !== rootB) return rootB - rootA
      const relA = a.relevance ?? a.score ?? 0
      const relB = b.relevance ?? b.score ?? 0
      return relB - relA
    })
  }

  return results
})

const resultsByDomain = computed(() => {
  const groups = new Map()
  for (const r of sortedResults.value) {
    const host = getResultHostnameForSort(r)
    const base = getBaseDomain(host)
    if (!groups.has(base)) groups.set(base, [])
    groups.get(base).push(r)
  }
  for (const arr of groups.values()) {
    arr.sort((a, b) => {
      const hostA = getResultHostnameForSort(a)
      const hostB = getResultHostnameForSort(b)
      const isBaseA = isBaseDomain(hostA)
      const isBaseB = isBaseDomain(hostB)
      if (isBaseA && !isBaseB) return -1
      if (!isBaseA && isBaseB) return 1
      const rootA = getRootPriority(a.url || a.link || '')
      const rootB = getRootPriority(b.url || b.link || '')
      if (rootA !== rootB) return rootB - rootA
      return (b.relevance ?? b.score ?? 0) - (a.relevance ?? a.score ?? 0)
    })
  }
  return Array.from(groups.entries()).map(([baseDomain, results]) => ({ baseDomain, results }))
})

const paginatedResults = computed(() => {
  const start = (currentPage.value - 1) * resultsPerPage.value
  const end = start + resultsPerPage.value
  return sortedResults.value.slice(start, end)
})

const paginatedResultsByDomain = computed(() => {
  const start = (currentPage.value - 1) * resultsPerPage.value
  const end = start + resultsPerPage.value
  const flat = resultsByDomain.value.flatMap(g => g.results)
  const slice = flat.slice(start, end)
  const groups = new Map()
  for (const r of slice) {
    const base = getBaseDomain(getResultHostnameForSort(r))
    if (!groups.has(base)) groups.set(base, [])
    groups.get(base).push(r)
  }
  return Array.from(groups.entries()).map(([baseDomain, results]) => ({ baseDomain, results }))
})

const visiblePages = computed(() => {
  const pages = []
  const maxVisible = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(calculatedTotalPages.value, start + maxVisible - 1)
  
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})

// Cache de favicons para melhor performance
const faviconCache = new Map()

const getFaviconUrl = (hostname) => {
  if (!hostname) return null
  
  // Verifica cache primeiro
  if (faviconCache.has(hostname)) {
    return faviconCache.get(hostname)
  }
  
  // Remove www. se presente para melhor compatibilidade
  const cleanHostname = hostname.replace(/^www\./, '')
  
  // Tenta diferentes formatos de favicon em ordem de prioridade
  const faviconPaths = [
    '/favicon.ico',
    '/favicon.png',
    '/apple-touch-icon.png',
    '/apple-touch-icon-precomposed.png',
    '/favicon-16x16.png',
    '/favicon-32x32.png'
  ]
  
  // Retorna o primeiro favicon disponível (https primeiro)
  const faviconUrl = `https://${cleanHostname}${faviconPaths[0]}`
  
  // Adiciona ao cache
  faviconCache.set(hostname, faviconUrl)
  
  return faviconUrl
}

const getSiteInitial = (hostname) => {
  if (!hostname) return '?'
  return hostname.charAt(0).toUpperCase()
}

const getResultHostname = (result) => {
  if (result.metadata?.hostname) return result.metadata.hostname
  if (result.hostname) return result.hostname
  try {
    return result.url ? new URL(result.url).hostname : null
  } catch {
    return null
  }
}

const getResultDate = (result) => {
  return result.metadata?.scrapedAt || result.created_at || result.timestamp
}

const getResultRelevance = (result) => {
  return result.relevance ?? result.score ?? null
}

const performSearch = async () => {
  if (!searchQuery.value.trim() || isSearching.value) return
  
  console.log('🔍 Iniciando busca por:', searchQuery.value)
  console.log('📱 P2PClient disponível:', !!p2pClient.value)
  console.log('🔌 Status da conexão:', p2pClient.value?.isConnected)

  syncQueryToUrl(searchQuery.value)
  
  const startTime = Date.now()
  isSearching.value = true
  // Não limpa resultados: mantém layout estável até nova resposta
  currentPage.value = 1
  filterDomain.value = ''
  filterDomain.value = ''
  
  try {
    // Verifica se está desconectado e tenta reconectar
    if (!p2pClient.value || !p2pClient.value.isConnected) {
      console.log('🔌 Cliente desconectado, tentando reconectar...')
      isReconnecting.value = true
      reconnectionAttempts.value = 0
      
      // Tenta reconectar até o máximo de tentativas
      while (reconnectionAttempts.value < maxReconnectionAttempts && (!p2pClient.value || !p2pClient.value.isConnected)) {
        reconnectionAttempts.value++
        console.log(`🔄 Tentativa de reconexão ${reconnectionAttempts.value}/${maxReconnectionAttempts}`)
        
        try {
          // Inicializa o cliente P2P
          p2pClient.value = new P2PClient()
          await p2pClient.value.connectToServer()
          
          // Aguarda um pouco para estabilizar a conexão
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          if (p2pClient.value.isConnected) {
            console.log('✅ Reconexão bem-sucedida!')
            break
          }
        } catch (error) {
          console.error(`❌ Tentativa ${reconnectionAttempts.value} falhou:`, error)
          
          if (reconnectionAttempts.value < maxReconnectionAttempts) {
            // Aguarda antes da próxima tentativa
            await new Promise(resolve => setTimeout(resolve, 2000))
          }
        }
      }
      
      isReconnecting.value = false
      
      if (!p2pClient.value || !p2pClient.value.isConnected) {
        throw new Error(`Falha na reconexão após ${maxReconnectionAttempts} tentativas`)
      }
    }
    
    if (!p2pClient.value) {
      throw new Error('P2PClient não foi inicializado')
    }
    
    // Configura callbacks para atualizar o status da conexão
    p2pClient.value.setCallbacks(
      handleResultsUpdate,
      handleProgressUpdate,
      handleConnectionChange
    )
    
    // Executa a busca
    await p2pClient.value.search(searchQuery.value.trim())
    
    searchTime.value = Date.now() - startTime
    console.log('✅ Busca concluída em', searchTime.value, 'ms')
    
  } catch (error) {
    console.error('❌ Erro na busca:', error)
    
    let errorMessage = 'Erro na busca'
    if (error.message.includes('reconexão')) {
      errorMessage = 'Falha na reconexão com o servidor. Tente novamente.'
    } else if (error.message.includes('P2PClient')) {
      errorMessage = 'Erro na inicialização do cliente. Recarregue a página.'
    } else {
      errorMessage = error.message || 'Erro desconhecido na busca'
    }
    
    searchProgress.value = {
      status: 'error',
      error: errorMessage,
      progress: 0,
      query: searchQuery.value,
      peersCount: 0,
      peersResponded: 0
    }
  } finally {
    isSearching.value = false
  }
}

const goToPage = (page) => {
  if (page >= 1 && page <= calculatedTotalPages.value) {
    currentPage.value = page
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

const formatDate = (timestamp) => {
  if (!timestamp) return 'Data desconhecida'
  const date = new Date(timestamp)
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

// Funções para lidar com favicons
const handleFaviconLoad = (event) => {
  // Favicon carregou com sucesso, esconde o fallback
  const fallback = event.target.nextElementSibling
  if (fallback) {
    fallback.style.display = 'none'
  }
}

const handleFaviconError = (event) => {
  // Favicon falhou, esconde a imagem e mostra o fallback
  event.target.style.display = 'none'
  const fallback = event.target.nextElementSibling
  if (fallback) {
    fallback.style.display = 'flex'
  }
}

// Callbacks do P2P Client
const handleResultsUpdate = (data) => {
  console.log('📥 handleResultsUpdate chamado com:', data)
  
  // Extrai os resultados do objeto recebido
  const { results, currentPage: page, totalResults: total } = data
  
  // Atualiza os dados de busca
  searchResults.value = results || []
  if ((results?.length || 0) > 0) hasEverHadSearchResults.value = true
  currentPage.value = page || 1
  totalResults.value = total || 0
  // totalPages é calculado automaticamente pelo computed
  
  console.log(`✅ ${results?.length || 0} resultados recebidos`)
  console.log(`📄 Página atual: ${page}, Total de páginas: ${calculatedTotalPages.value}, Total de resultados: ${total}`)
  console.log(`🔍 searchResults.value agora tem ${searchResults.value.length} itens`)
}

const handleProgressUpdate = (progress) => {
  console.log('📊 handleProgressUpdate chamado com:', progress)
  
  // Extrai os dados de progresso
  const { status, query, peersCount, progress: progressValue, totalResults: total, duration } = progress
  
  // Atualiza o progresso
  searchProgress.value = {
    status: status || 'idle',
    query: query || '',
    peersCount: peersCount || 0,
    progress: progressValue || 0,
    totalResults: total || 0,
    duration: duration || 0
  }
  
  // Atualiza o tempo de busca quando completada
  if (status === 'completed' && duration) {
    searchTime.value = duration
  }
  
  // Atualiza o status no App.vue
  updateLocalConnectionStatus()
  
  console.log(`📊 Progresso atualizado:`, searchProgress.value)
}

const handleConnectionChange = (isConnected) => {
  console.log('🔌 handleConnectionChange chamado com:', isConnected)
  
  if (isReconnecting.value && isConnected) {
    // Reconexão bem-sucedida
    isReconnecting.value = false
    reconnectionAttempts.value = 0
    console.log('✅ Reconexão automática bem-sucedida!')
  }
  
  updateLocalConnectionStatus(isConnected ? 'Conectado' : 'Desconectado')
  console.log('🔌 Status da conexão:', isConnected ? 'Conectado' : 'Desconectado')
}

// Função para atualizar o status no App.vue
const updateLocalConnectionStatus = (status) => {
  if (p2pClient.value) {
    const currentStatus = {
      isConnected: p2pClient.value.isConnected,
      currentQuery: searchProgress.value.query || '',
      status: status || (p2pClient.value.isConnected ? 'Conectado' : 'Desconectado')
    }
    
    // Chama a função injetada do App.vue
    updateAppConnectionStatus(currentStatus)
  }
}

// Inicialização
onMounted(async () => {
  resetHomeRef.value = resetToInitial
  console.log('🚀 Inicializando BuscaLogo...')
  
  try {
    // Inicializa cliente P2P
    p2pClient.value = new P2PClient()
    console.log('📱 P2PClient criado:', p2pClient.value)
    
    // Configura callbacks
    console.log('🔧 Configurando callbacks...')
    p2pClient.value.setCallbacks(
      handleResultsUpdate,
      handleProgressUpdate,
      handleConnectionChange
    )
    console.log('✅ Callbacks configurados')
    
    // Conecta ao servidor
    console.log('🔌 Conectando ao servidor...')
    await p2pClient.value.connectToServer()
    console.log('✅ BuscaLogo inicializado')
    
    // Verifica status da conexão
    console.log('🔍 Status da conexão:', p2pClient.value.isConnected)
    
    // Atualiza o status no App.vue
    updateLocalConnectionStatus()

    // Deep-link: /search?q=termo (ou redirect de /?q=)
    const initialQ = readQueryParam()
    if (initialQ) {
      searchQuery.value = initialQ
      await performSearch()
    }
  } catch (error) {
    console.error('❌ Erro na inicialização:', error)
    console.error('Stack trace:', error.stack)
    
    // Atualiza o status de erro no App.vue
    updateLocalConnectionStatus()
  }
})

// Navegação do browser (voltar/avançar) com ?q=
watch(
  () => route.query.q,
  async (q) => {
    const term = typeof q === 'string' ? q.trim() : ''
    if (!term) return
    if (term === searchQuery.value.trim() && (isSearching.value || hasEverHadSearchResults.value)) {
      return
    }
    searchQuery.value = term
    await performSearch()
  }
)

onUnmounted(() => {
  if (resetHomeRef?.value === resetToInitial) resetHomeRef.value = null
})
</script>
